/**
 * Who in DSAI has a routed-onward demand: its cluster and its caretaker.
 *
 * "DSAI" is what the product used to call the Digital Council (Paula,
 * 2026-09-21) — in practice "not a DAC matter". The v1 "New demand List" gave
 * every row a `DSAI Cluster` (Analytics/Nanda, CAIDO/Alex, …) and a caretaker;
 * this is that, for the demands we pass on.
 *
 * `canAssign` is the SERVER's answer (the queue's `can_assign`, a demand's
 * `viewer.can_edit`). With it, a cluster select and a caretaker picker; without
 * it, the same two facts as plain text. Both save through PATCH /demands/{id},
 * so each change lands in the demand's timeline with who made it.
 */

import { Select, makeStyles, tokens } from '@fluentui/react-components'
import { PersonPicker } from './PersonPicker'
import { useToast } from './ToastProvider'
import { useDsaiClusters } from '../hooks/useDigitalCouncil'
import { useUpdateDemand } from '../hooks/useDemands'
import { useMentionableUsers } from '../hooks/useMentionableUsers'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { apiErrorMessage } from '../lib/apiError'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  key: { minWidth: '88px', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  empty: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
})

export interface DsaiAssignmentProps {
  demandId: number
  cluster?: string | null
  /** How the cluster is written for a reader — "Data (Kerstin)" — when the
   *  caller already has it; otherwise looked up from the served list. */
  clusterLabel?: string | null
  caretakerId?: number | null
  caretakerName?: string | null
  canAssign: boolean
  /** The demand page has its own caretaker picker in the People card. */
  showCaretaker?: boolean
}

export function DsaiAssignment({
  demandId,
  cluster,
  clusterLabel,
  caretakerId,
  caretakerName,
  canAssign,
  showCaretaker = true,
}: DsaiAssignmentProps) {
  const styles = useStyles()
  const { showToast } = useToast()
  const me = useEffectiveUser()
  const update = useUpdateDemand()
  const { data: clusters = [] } = useDsaiClusters()
  // A DSAI caretaker is usually not one of ours, so the list is only the
  // admins; "Assign to me" and a freehand name cover everyone else.
  const { data: people = [] } = useMentionableUsers('UN', canAssign && showCaretaker)

  const label = clusterLabel ?? clusters.find((c) => c.code === cluster)?.label ?? cluster

  function save(data: Parameters<typeof update.mutate>[0]['data']) {
    update.mutate(
      { id: demandId, data },
      {
        onError: (err) =>
          showToast({ title: 'Could not save the assignment', body: apiErrorMessage(err), intent: 'error' }),
      },
    )
  }

  return (
    <div className={styles.root} data-testid={`dsai-assignment-${demandId}`}>
      <div className={styles.row}>
        <span className={styles.key}>DSAI cluster</span>
        {canAssign ? (
          <Select
            value={cluster ?? ''}
            disabled={update.isPending}
            aria-label="DSAI cluster"
            data-testid={`dsai-cluster-select-${demandId}`}
            onChange={(_, d) => save({ dsai_cluster: d.value || null })}
          >
            <option value="">Not assigned yet</option>
            {clusters.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </Select>
        ) : (
          <span className={label ? undefined : styles.empty} data-testid={`dsai-cluster-${demandId}`}>
            {label ?? 'Not assigned yet'}
          </span>
        )}
      </div>

      {showCaretaker && (
        <div className={styles.row}>
          <span className={styles.key}>Caretaker</span>
          <PersonPicker
            currentName={caretakerName}
            userId={caretakerId}
            roleLabel="Caretaker"
            avatarColor="#6b21a8"
            users={people}
            disabled={!canAssign || update.isPending}
            allowFreehand
            selfUser={me?.id && me.name ? { id: me.id, name: me.name } : null}
            onSelect={(u) =>
              save({ caretaker_name: u ? u.name : null, caretaker_id: u ? u.id : null })
            }
            onFreehandSelect={(name) => save({ caretaker_name: name, caretaker_id: null })}
          />
        </div>
      )}
    </div>
  )
}
